import csvDownload from 'json-to-csv-export';
import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiErrorHandling } from 'src/api/apierrorhandling';
import { getAfACSV } from 'src/api/pmApi';
import { getCSVDebug, getYearlyTaxFormE6B, getYearlyTaxFormVerf60 } from 'src/api/stoApi';
import { IAssetYearlyTaxReports } from 'src/models';
import { awaitUtil } from 'src/util/utils';
import BorderAllIcon from '@mui/icons-material/BorderAll';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { Button, IconButton, TableCell, TableRow, TextField } from '@mui/material';
import Grid from '@mui/material/Grid';

interface Props {
  yearlyTaxReport: IAssetYearlyTaxReports;
  index: number;
  editTaxReport: (taxReportIndex: number) => void;
}

export const SingleYearlyTaxReport: FC<Props> = ({ yearlyTaxReport, editTaxReport, index }) => {
  const { assetId } = useParams<{ assetId: string }>();
  const [userId, setUserId] = useState('');

  const handleUserIdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUserId(event.target.value);
  };

  const renderEditIcon = (taxReportId: number) => {
    return (
      <IconButton aria-label="delete" style={{ verticalAlign: 'middle' }} onClick={() => editTaxReport(taxReportId)}>
        <ModeEditIcon />
      </IconButton>
    );
  };
  const renderTaxFormVerf60DownloadIcon = (year: string) => {
    return (
      <Button
        aria-label="delete"
        startIcon={<PictureAsPdfIcon />}
        style={{ verticalAlign: 'middle' }}
        onClick={() => downloadTaxFormVerf60AsPDF(year)}
      >
        Verf60
      </Button>
    );
  };
  const renderTaxFormE6bDownloadIcon = (year: string) => {
    return (
      <Button
        aria-label="delete"
        startIcon={<PictureAsPdfIcon />}
        style={{ verticalAlign: 'middle' }}
        onClick={() => downloadTaxFormE6bAsPDF(year)}
      >
        E 6b
      </Button>
    );
  };
  const renderDebugCsv = (year: string) => {
    return (
      <Button aria-label="delete" startIcon={<BorderAllIcon />} style={{ verticalAlign: 'middle' }} onClick={() => downloadDebugCSV(year)}>
        .csv
      </Button>
    );
  };
  const renderDebugAfACsv = (year: string) => {
    return (
      <>
        <TextField label="User Id" size="small" placeholder="f23453" value={userId} onChange={handleUserIdChange} sx={{ width: '50%' }} />
        <Button aria-label="delete" startIcon={<BorderAllIcon />} style={{ verticalAlign: 'middle' }} onClick={() => downloadAfACSV(year)}>
          .afa
        </Button>
      </>
    );
  };

  const downloadTaxFormVerf60AsPDF = async (year: string) => {
    if (assetId) {
      try {
        const [data, error] = await awaitUtil(getYearlyTaxFormVerf60(assetId, year));
        if (error) return;
        if (!data) return;
        const url = window.URL.createObjectURL(new Blob([data.data]));
        const link = document.createElement('a');
        const fileName = `Verf60_${assetId}_${year}.pdf`;
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (error) {
        apiErrorHandling(error);
      }
    }
  };
  const downloadTaxFormE6bAsPDF = async (year: string) => {
    if (assetId) {
      try {
        const [data, error] = await awaitUtil(getYearlyTaxFormE6B(assetId, year));
        if (error) return;
        if (!data) return;
        const url = window.URL.createObjectURL(new Blob([data.data]));
        const link = document.createElement('a');
        const fileName = `E6b_${assetId}_${year}.zip`;
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (error) {
        apiErrorHandling(error);
      }
    }
  };

  const downloadDebugCSV = async (year: string) => {
    if (assetId) {
      try {
        const [data, error] = await awaitUtil(getCSVDebug(assetId, year));
        if (error) return;
        if (!data) return;
        const perUser = data.data.data.amountsWithOwnershipPercentage;
        const sum = data.data.data.sumAmountsWithOwnershipPercentage;
        const perUserArray = Object.keys(perUser).map((user: any) => {
          return [
            user,
            perUser[user].firstName,
            perUser[user].lastName,
            perUser[user].totalPayouts,
            perUser[user].amount,
            perUser[user].percentage,
            perUser[user].AfA,
          ];
        });
        perUserArray.push([
          'total shares: ',
          sum.sumOfTotalPayouts,
          'total amount €: ',
          sum.sumOfAmounts,
          'total percentage investors',
          sum.sumOfPercentage,
          '',
        ]);
        perUserArray.push(['sumOfPayouts', data.data.data.payouts, '', '', '', '', '']);
        const dataToConvert = {
          data: perUserArray,
          filename: 'investor_tax_report',
          delimiter: ';',
          headers: ['user', 'name', 'lastname', 'shares paid out', 'amount €', 'percentage', 'AfA'],
        };
        csvDownload(dataToConvert);
      } catch (error) {
        apiErrorHandling(error);
      }
    }
  };

  const downloadAfACSV = async (year: string) => {
    if (!assetId) throw new Error('assetId is not defined');
    const [data, error] = await awaitUtil(getAfACSV(userId, assetId, year));
    if (data) {
      const url = window.URL.createObjectURL(new Blob([data.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      const fileName = `afa_for_${userId}_${year}_${assetId}.csv`;
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    }
    if (error) {
      apiErrorHandling(error);
    }
  };
  return (
    <TableRow key={yearlyTaxReport.createdAt + index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
      <TableCell data-testid={index + '-year'} component="th" scope="row">
        {yearlyTaxReport.year}
      </TableCell>
      <TableCell data-testid={index + '-income'} align="left">
        {yearlyTaxReport.income.$numberDecimal}
      </TableCell>
      <TableCell data-testid={index + '-megData.taxOffice'} align="left">
        {yearlyTaxReport.megData?.taxOffice || ''}
      </TableCell>
      <TableCell data-testid={index + '-megData.taxNumber'} align="left">
        {yearlyTaxReport.megData?.taxNumber || ''}
      </TableCell>
      <TableCell data-testid={index + '-megData.taxOfficeTeam'} align="left">
        {yearlyTaxReport.megData?.taxOfficeTeam || ''}
      </TableCell>
      <TableCell data-testid={index + '-megData.megName'} align="left">
        {yearlyTaxReport.megData?.megName || ''}
      </TableCell>
      <TableCell data-testid={index + '-megData.taxRepresentative'} align="left">
        {yearlyTaxReport.megData?.taxRepresentative || ''}
      </TableCell>
      <TableCell data-testid={index + '-megData.downloads'} align="right">
        <Grid container spacing={0}>
          <Grid>{renderEditIcon(index)}</Grid>
          <Grid>{renderTaxFormVerf60DownloadIcon(yearlyTaxReport.year)}</Grid>
          <Grid>{renderTaxFormE6bDownloadIcon(yearlyTaxReport.year)}</Grid>
          <Grid>{renderDebugCsv(yearlyTaxReport.year)}</Grid>
          <Grid xs={6}>{renderDebugAfACsv(yearlyTaxReport.year)}</Grid>
        </Grid>
      </TableCell>
    </TableRow>
  );
};
